import {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {StoreContext} from "../components/StoreContextProvider";
import ServiceClient from "../utils/ServiceClient";
import {WagerMode} from "../../generated/bay_session_shared_pb";
import {
  SelectableWagerCategory,
  currencyForCategory,
  resolveDefaultCategory,
  resolveSelectedCategory,
  selectableCategories,
  shouldShowCurrencySelector,
  switchTargetForCategory,
} from "../utils/wagerCategory";

export type UseWagerCurrencyResult = {
  /** The currency categories (MONEY / POINTS) the station offers this player. */
  options: SelectableWagerCategory[];
  /**
   * Single-availability rule (SMG-3446 AC): true only when ≥2 categories are
   * selectable. The selector UI renders ONLY when this is true; with exactly
   * one category the sole option is auto-used and the selector is hidden.
   */
  showSelector: boolean;
  /**
   * The effective selected category — the explicit in-session choice when ≥2
   * are offered, else the auto-selected sole category, else undefined when no
   * currency wager is possible (e.g. PRACTICE-only / unresolved tier).
   */
  selectedCategory: SelectableWagerCategory | undefined;
  /**
   * The wallet currency string to stamp on AcceptBet for the selected
   * category. 'USD' for Real Money; '' for Points (server resolves the token
   * from the sticky next_wager_currency + venue config). undefined when no
   * category is selectable.
   */
  resolvedCurrency: string | undefined;
  /**
   * Choose a category. With NO live wager → SetWagerMode(category). With a
   * live/open wager → SwitchWagerCurrency(targetToken). The live-wager
   * decision is made from Context.bayActiveBetId (stream-driven active bet).
   * Returns true on success.
   */
  selectCategory: (category: SelectableWagerCategory) => Promise<boolean>;
  isLoading: boolean;
  /**
   * SMG-3446: true ONLY when both categories are available, the player has made
   * no explicit selection this session, and their available USD balance is
   * below the $5 ($500 minor) Real Money floor — so we defaulted them to Points
   * instead of Real Money. The UI renders an informational banner while this is
   * true. It clears when the player manually switches to Real Money (they're
   * allowed to try — the server-side funds guard is the real gate) or dismisses
   * the banner.
   */
  autoMovedToPointsInsufficientUSD: boolean;
  /**
   * Dismiss the "moved to Points" funds banner without changing the selected
   * category. Clears BOTH the client-side dismissal flag and the server-driven
   * Context flag (SMG-3455) so a single dismiss hides the banner regardless of
   * which trigger raised it.
   */
  dismissInsufficientUSDBanner: () => void;
  /**
   * SMG-3455: true when the SERVER reported the player cannot fund the next
   * wager and there is NO points fallback (reason PLAYER_INSUFFICIENT_FUNDS).
   * Drives the NEW "out of funds" banner. Purely server-driven — there is no
   * client-side heuristic for this state. Clears when funds recover (Main.tsx)
   * or on dismiss.
   */
  insufficientFundsNoFallback: boolean;
  /** SMG-3455: dismiss the new "out of funds" banner. */
  dismissInsufficientFundsBanner: () => void;
};

/**
 * useWagerCurrency — the SMG-3446/3447 currency CATEGORY picker controller.
 *
 * Availability is sourced from SessionPlayer.allowed_wager_modes (bridged onto
 * Context.allowedWagerModes by Main.tsx's per-user stream dispatcher). The hook
 * applies the single-availability rule and drives the right RPC:
 *   - no live wager  → PlayerBaySessionService.SetWagerMode(category)
 *   - live wager     → PlayerBaySessionService.SwitchWagerCurrency(targetToken)
 *
 * The category→token mapping is intentionally coarse on the client: MONEY→USD,
 * POINTS→'' (server resolves the concrete TGPTS / FSPTS from venue config).
 */
export function useWagerCurrency(): UseWagerCurrencyResult {
  const Context = useContext(StoreContext);
  const {
    allowedWagerModes,
    activeWagerMode,
    bayActiveBetId,
    bayPlayerJwt,
    stationId,
    usdAvailableMinor,
    // SMG-3455: server-driven funding-fallback flags (set by Main.tsx from the
    // per-user PlayerStateUpdate stream). serverMovedToPointsInsufficientFunds
    // reconciles with the client-side autoMovedToPointsInsufficientUSD heuristic
    // (banner shows for EITHER); insufficientFundsNoFallback drives the new
    // "out of funds" banner.
    serverMovedToPointsInsufficientFunds,
    setServerMovedToPointsInsufficientFunds,
    insufficientFundsNoFallback,
    setInsufficientFundsNoFallback,
  } = Context;

  const [explicitChoice, setExplicitChoice] = useState<SelectableWagerCategory | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  // The player dismissed the insufficient-USD banner. Sticky for the session so
  // the banner doesn't re-appear on the next stream frame.
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const options = useMemo(() => selectableCategories(allowedWagerModes), [allowedWagerModes]);
  const showSelector = useMemo(() => shouldShowCurrencySelector(allowedWagerModes), [allowedWagerModes]);

  // SMG-3446: the funds-aware default applies ONLY when BOTH categories are
  // available (the selector-shown case) and the player has made no explicit
  // selection this session. Default to Real Money unless available USD is below
  // the $5 floor, in which case move to Points and flag the banner.
  const fundsDefault = useMemo(
    () => (showSelector ? resolveDefaultCategory(usdAvailableMinor) : undefined),
    [showSelector, usdAvailableMinor],
  );

  // The effective default category fed into the resolver. With both available
  // and no explicit choice, this is the funds-aware default; otherwise the
  // existing rules (server wager_mode / first-offered) stand. Single-
  // availability is unchanged — resolveSelectedCategory forces the sole option.
  const selectedCategory = useMemo(() => {
    if (showSelector && explicitChoice === undefined && fundsDefault !== undefined) {
      return fundsDefault.category;
    }
    return resolveSelectedCategory(allowedWagerModes, activeWagerMode, explicitChoice);
  }, [showSelector, explicitChoice, fundsDefault, allowedWagerModes, activeWagerMode]);

  const resolvedCurrency = useMemo(
    () => (selectedCategory === undefined ? undefined : currencyForCategory(selectedCategory)),
    [selectedCategory],
  );

  // Banner: shown while we moved the player to Points for insufficient funds —
  // via EITHER the client-side USD<$5 heuristic OR the authoritative server
  // reason (SMG-3455 PLAYER_MOVED_TO_POINTS_INSUFFICIENT_FUNDS). Suppressed once
  // the player makes an explicit selection or dismisses it. The server flag is
  // authoritative, so it surfaces the banner even when the client heuristic did
  // not fire (e.g. USD >= $5 but the next bet still couldn't be funded).
  const movedToPointsTrigger =
    fundsDefault?.autoMovedToPointsInsufficientUSD === true ||
    serverMovedToPointsInsufficientFunds === true;
  const autoMovedToPointsInsufficientUSD =
    explicitChoice === undefined && !bannerDismissed && movedToPointsTrigger;

  // Auto-persist the funds-aware default when we MOVE the player to Points for
  // insufficient USD, so their NEXT wager actually goes to Points (not Real
  // Money). We deliberately do NOT auto-persist the plain Real Money default:
  // MONEY is the server's typical resolution for a both-available player, so
  // arming it explicitly would be redundant RPC traffic — only the *move* needs
  // persisting. Idempotent: a ref tracks the persisted category, and we no-op
  // when the server already reflects POINTS, so a sub-second stream re-render
  // does not spam the RPC.
  const persistedDefaultRef = useRef<SelectableWagerCategory | undefined>(undefined);
  useEffect(() => {
    // Only the both-available, no-explicit-choice, auto-moved-to-Points case
    // auto-persists.
    if (!showSelector || explicitChoice !== undefined) return;
    if (fundsDefault?.autoMovedToPointsInsufficientUSD !== true) return;
    const target = fundsDefault.category; // POINTS
    // Already persisted → no-op (idempotent).
    if (persistedDefaultRef.current === target) return;
    // Server already on Points → nothing to persist; record it so we don't
    // re-issue when the value-equal stream frame ticks.
    if (activeWagerMode === target) {
      persistedDefaultRef.current = target;
      return;
    }
    // Needs creds + station to persist; skip until they're ready (the effect
    // re-runs when they populate from the stream).
    if (!bayPlayerJwt) return;
    const station = BigInt(stationId || 0);
    if (station === 0n) return;
    // A live wager would require the funds-guarded SwitchWagerCurrency. The
    // default-apply only arms the NEXT category via SetWagerMode; with a live
    // wager we leave the existing wager untouched and let the player switch
    // explicitly. Auto-apply is for the no-live-wager arming case.
    const hasLiveWager = bayActiveBetId !== '' && bayActiveBetId !== '0';
    if (hasLiveWager) return;

    persistedDefaultRef.current = target;
    ServiceClient.setWagerMode(station, target as WagerMode, bayPlayerJwt).catch((err: any) => {
      // Roll back the marker so a later frame can retry the persist.
      persistedDefaultRef.current = undefined;
      surfaceError(Context, err?.message);
    });
  }, [showSelector, explicitChoice, fundsDefault, activeWagerMode, bayActiveBetId, bayPlayerJwt, stationId, Context]);

  const dismissInsufficientUSDBanner = () => {
    setBannerDismissed(true);
    // SMG-3455: also clear the server-driven flag so the banner stays hidden
    // even though the next stream frame may still carry the same reason; a
    // subsequent funds-recovery / no-fallback frame (Main.tsx) re-derives it.
    if (serverMovedToPointsInsufficientFunds) {
      setServerMovedToPointsInsufficientFunds(false);
    }
  };

  // SMG-3455: dismiss the new "out of funds" (no-fallback) banner. Purely
  // server-driven, so dismissal clears the Context flag directly; Main.tsx
  // re-raises it if a later frame still reports PLAYER_INSUFFICIENT_FUNDS.
  const dismissInsufficientFundsBanner = () => {
    if (insufficientFundsNoFallback) {
      setInsufficientFundsNoFallback(false);
    }
  };

  const selectCategory = async (category: SelectableWagerCategory): Promise<boolean> => {
    // Record the desired choice immediately so the picker reflects intent
    // even before the RPC resolves; the stream re-emits server truth onto
    // Context.activeWagerMode and reconciles any skew. An explicit selection
    // also clears the funds banner — the player has taken control (incl. trying
    // Real Money, which the server-side funds guard will gate at accept/switch).
    setExplicitChoice(category);
    // SMG-3455: an explicit selection also clears the server-driven
    // moved-to-points flag. autoMovedToPointsInsufficientUSD already gates on
    // explicitChoice, but the Context flag is shared state, so clear it too so a
    // later re-render (or another reader) doesn't resurface a banner the player
    // already overrode.
    if (serverMovedToPointsInsufficientFunds) {
      setServerMovedToPointsInsufficientFunds(false);
    }

    if (!bayPlayerJwt) {
      surfaceError(Context, 'Bay-session player JWT missing — please re-authenticate');
      return false;
    }
    const station = BigInt(stationId || 0);
    if (station === 0n) {
      surfaceError(Context, 'Station not yet ready — try again in a moment');
      return false;
    }

    try {
      setIsLoading(true);
      const hasLiveWager = bayActiveBetId !== '' && bayActiveBetId !== '0';
      if (hasLiveWager) {
        // SwitchWagerCurrency is the sanctioned switch path with an open
        // wager (SMG-3233 blocks a bare SetWagerMode flip there).
        await ServiceClient.switchWagerCurrency(station, switchTargetForCategory(category), bayPlayerJwt);
      } else {
        // No open wager → set the sticky next category; the server derives
        // the concrete points token from venue config.
        await ServiceClient.setWagerMode(station, category as WagerMode, bayPlayerJwt);
      }
      return true;
    } catch (err: any) {
      surfaceError(Context, err?.message);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    options,
    showSelector,
    selectedCategory,
    resolvedCurrency,
    selectCategory,
    isLoading,
    autoMovedToPointsInsufficientUSD,
    dismissInsufficientUSDBanner,
    insufficientFundsNoFallback,
    dismissInsufficientFundsBanner,
  };
}

function surfaceError(Context: {setCancelModalConfig: (cfg: any) => void}, message?: string) {
  if (!message) return;
  Context.setCancelModalConfig({
    visible: true, title: 'ERROR', subtitle: message, rightBtnText: 'OK',
    onResume: () => Context.setCancelModalConfig((prev: any) => ({...prev, visible: false})),
    onClose: () => Context.setCancelModalConfig((prev: any) => ({...prev, visible: false})),
  } as any);
}
